import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  effect,
  inject,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { faAnglesUp } from '@fortawesome/pro-light-svg-icons';
import { faCheck as faCheckRegular } from '@fortawesome/pro-regular-svg-icons';
import { faCheck as faCheckSolid } from '@fortawesome/pro-solid-svg-icons';
import interact from 'interactjs';
import { SubSink } from 'subsink';
import { assert } from '../../../core/assert/assert';
import { Priorities } from '../../../core/constants-repository/priority-values.constants';
import { TodoEntryModel } from '../../../core/models/todo-planner/todo-entry.model';
import { TodoPlannerHttpService } from '../../../core/state/todo-planner/todo-planner-http.service';
import { getPriorityLabel } from '../../../core/text-db/priority-values.text';
import { IFixIntervalViewType } from '../../calendar/components/time-block/types/time-block-types';
import { DateTimeHelper } from '../../calendar/util/date-time-helper';
import { TodoPlannerService } from '../services/todo-planner.service';
import {
  RestrictionAreaCSSClass,
  TodoEntryFixIntervalCSSClass,
  TodoEntryHost,
  TodoEntryVariableIntervalCSSClass,
  TodoEntryWrapperCSSClass,
  TodoPlannerFixIntervalContainerGap,
} from '../utils/todo-planner.constants';
import { TodoEntryDragService } from './services/drag-and-drop/todo-entry-drag.service';
import { TodoEntryCrudService } from './services/todo-entry-crud.service';
import { TodoEntryInteractionService } from './services/todo-entry-interaction.service';
import { TodoEntryService } from './services/todo-entry.service';

@Component({
  selector: 'app-todo-entry',
  templateUrl: './todo-entry.component.html',
  styleUrl: './todo-entry.component.scss',
  standalone: false,
  host: { class: TodoEntryHost },
})
export class TodoEntryComponent implements OnInit, AfterViewInit, OnDestroy {
  // Signals and view child
  readonly todoEntryWrapper = viewChild<ElementRef<HTMLDivElement>>('todoEntryWrapper');
  readonly todoEntryWidth = input<number>(null);
  readonly todoEntryModel = model.required<TodoEntryModel>();
  readonly startsBeforePlannerStart = signal(false);
  readonly endsAfterPlannerEnd = signal(false);
  readonly priorityText = signal('');
  readonly todoEntryTop = signal<number>(0); // For fix interval to-do entries.
  readonly parallelIndex = signal(0);
  readonly hideDetails = signal(false);

  // Constants
  public readonly faAnglesUp = faAnglesUp;
  public readonly faCheckRegular = faCheckRegular;
  public readonly faCheckSolid = faCheckSolid;
  public readonly TodoEntryWrapperCSSClass = TodoEntryWrapperCSSClass;

  // DI
  private readonly todoEntryService = inject(TodoEntryService);
  private readonly todoEntryCrudService = inject(TodoEntryCrudService);
  private readonly todoPlannerService = inject(TodoPlannerService);
  private readonly todoEntryDragService = inject(TodoEntryDragService);
  private readonly todoEntryInteractionService = inject(TodoEntryInteractionService);
  private readonly todoPlannerHttpService = inject(TodoPlannerHttpService);

  // Misc
  private readonly subs = new SubSink();

  constructor() {
    effect(() => {
      const model = this.todoEntryModel();
      const realStart = this.todoEntryService.getCorrectContainerDate(model.start);
      if (DateTimeHelper.isBefore(model.start, realStart)) {
        this.startsBeforePlannerStart.set(true);
      }
      const plannerEnd = this.todoPlannerService.todoPlannerModel.plannerEnd;
      if (DateTimeHelper.isAfter(model.end, plannerEnd)) {
        this.endsAfterPlannerEnd.set(true);
      }
    });
  }

  ngOnInit(): void {
    const hideDetails = !this.todoPlannerService.todoPlannerModel.hideTodoEntryDetails;
    this.hideDetails.set(hideDetails);
  }

  ngAfterViewInit(): void {
    // Set priority text
    const text = getPriorityLabel(this.todoEntryModel().priority as Priorities);
    this.priorityText.set(text);

    const intervalType = this.todoEntryModel().fixInterval
      ? TodoEntryFixIntervalCSSClass
      : TodoEntryVariableIntervalCSSClass;

    const restrictionElement = document.querySelector(
      `.${RestrictionAreaCSSClass}.${intervalType}`,
    ) as HTMLElement;

    assert(restrictionElement !== null);

    const originalEl = this.todoEntryWrapper().nativeElement;

    if (this.todoEntryModel().fixInterval) {
      this.initParallelIndexChange();
      return;
    }

    // Initialize dragging via interact.js.
    this.todoEntryDragService.initializeDragAndDrop(
      this.todoEntryModel().componentRef,
      restrictionElement,
    );

    // Create clone and drag it after clicking on a to-do entry.
    this.todoEntryInteractionService.handlePointerDown(
      originalEl,
      restrictionElement,
      this.todoEntryModel(),
    );
  }

  ngOnDestroy(): void {
    if (this.todoEntryWrapper().nativeElement) {
      // removes ALL interact listeners + interactables for this element
      interact(this.todoEntryWrapper().nativeElement).unset();
    }

    this.subs.unsubscribe();
  }

  public complete($event: PointerEvent): void {
    $event.stopPropagation();

    // clone the model, but with component ref. That is because we don't rerender the to-do entry and therefore need the component ref
    // for container high calculation later.
    let clonedModel = TodoEntryService.cloneTodoModel(this.todoEntryModel(), false);
    clonedModel.completed = !clonedModel.completed;
    this.todoEntryCrudService.updateTodoEntry(clonedModel);

    // For the upcoming http put request, remove the component ref.
    clonedModel = TodoEntryService.cloneTodoModel(clonedModel);
    clonedModel.reRenderComponent = false; // Don't remove and add the component, only update the model.
    this.todoPlannerHttpService.updateTodoPlannerEntry(clonedModel);

    this.todoEntryModel.set(clonedModel);
  }

  private initParallelIndexChange() {
    this.subs.sink = this.todoPlannerService.parallelIndexChanged$.subscribe(() => {
      const parallelIndex = (this.todoEntryModel().timeBlockViewType as IFixIntervalViewType)
        .parallelIndex;

      if (parallelIndex < 0) {
        return;
      }

      this.parallelIndex.set(parallelIndex);

      const top =
        parallelIndex === 0
          ? TodoPlannerFixIntervalContainerGap
          : parallelIndex * this.todoEntryWrapper().nativeElement.getBoundingClientRect().height +
            TodoPlannerFixIntervalContainerGap;
      this.todoEntryTop.set(top);
    });
  }
}
